import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getAuthUser } from 'store/selectors/auth';
import { getCommunities } from 'store/selectors/communities';
import { selectCurrentUserSubscription } from 'store/selectors/subscriptions';
import {
  SubscriptionFrequency,
  SubscriptionStatus,
  UserSubscription,
} from 'types';
import { formatSubscriptionDate } from 'utils/dates';
import { formatMoney } from 'utils/money';
import {
  getBillingAttentionActionUrl,
  getBillingPortalUrl,
  getCardExpiryParts,
  getInvoiceContactEmail,
  getLastInvoiceMeta,
  getLastInvoicePdfUrl,
  getPaymentMethodSummary,
} from 'utils/subscriptionBilling';
import {
  getBillingAttentionReason,
  getStripeSubscriptionEndIso,
  isChfSubscriptionCurrency,
  isEnterprisePaidPlan,
  isInvoiceBillingLayout,
  isLegacyAppCancelled,
  isPaidPlanTrialPeriod,
  isStripeNonRenewingActive as isStripeNonRenewingActiveFn,
  isStripeSubscriptionFullyEnded,
} from 'utils/subscriptionState';
import { isRenderableUserSubscription } from 'utils/subscriptionSync';
import { getUsedMembersCount } from 'utils/subscriptionUsage';

export function useSubscriptionProfileModel() {
  const { t, i18n } = useTranslation();
  const subscriptionRaw = useSelector(selectCurrentUserSubscription);
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);

  const lastRenderableSubscriptionRef = useRef<UserSubscription | null>(null);
  const subscription = useMemo(() => {
    if (isRenderableUserSubscription(subscriptionRaw as UserSubscription)) {
      lastRenderableSubscriptionRef.current =
        subscriptionRaw as UserSubscription;
      return subscriptionRaw as UserSubscription;
    }
    return lastRenderableSubscriptionRef.current ?? subscriptionRaw;
  }, [subscriptionRaw]);

  const groupsCount = useMemo(() => {
    if (communities.length > 0 && profile) {
      const owned = communities.filter(
        (item) => profile?.userId === item?.communityOwner?.userId,
      );
      return owned.reduce(
        (acc, item) =>
          acc +
          Math.max(Number(item.groupsCount) || 0, item.groups?.length || 0),
        0,
      );
    }
    return 0;
  }, [communities, profile]);

  const ownedCommunitiesCount = useMemo(() => {
    if (!profile?.userId || communities.length === 0) return 0;
    return communities.filter(
      (item) => profile.userId === item.communityOwner?.userId,
    ).length;
  }, [communities, profile?.userId]);

  const subscriptionEndDate = useMemo(() => {
    const endIso = getStripeSubscriptionEndIso(
      subscription as UserSubscription | null | undefined,
    );
    if (!endIso) return '-';
    return formatSubscriptionDate(endIso, i18n.language);
  }, [subscription, i18n.language]);

  const translatedPaymentCycle = useMemo(() => {
    if (subscription?.frequency === SubscriptionFrequency.MONTH) {
      return t('subscriptions.profile.labels.paymentCycleMonth');
    }
    if (subscription?.frequency === SubscriptionFrequency.YEAR) {
      return t('subscriptions.profile.labels.paymentCycleYear');
    }
    return '';
  }, [subscription, t]);

  const nextPaymentLabel = useMemo(() => {
    if (
      isLegacyAppCancelled(subscription as UserSubscription | undefined) ||
      subscription?.planId === SubscriptionStatus.TRIAL
    ) {
      return t('subscriptions.profile.labels.validUntil');
    }
    return t('subscriptions.profile.labels.nextPayment');
  }, [subscription, t]);

  const isFullyCancelled = isStripeSubscriptionFullyEnded(
    subscription as UserSubscription | undefined,
  );
  const isLegacyCancelled = isLegacyAppCancelled(
    subscription as UserSubscription | undefined,
  );

  const planName = useMemo(() => {
    if (subscription?.id) {
      if (isLegacyCancelled) {
        return t('subscriptions.cancelledTitle');
      }
      if (subscription.planId === SubscriptionStatus.TRIAL) {
        return t('subscriptions.trial');
      }
      if (subscription.subscriptionPlan) {
        return subscription.subscriptionPlan.productName;
      }
      return subscription.planId;
    }
    return t('subscriptions.free');
  }, [subscription, t, isLegacyCancelled]);

  const isTrial =
    Boolean(subscription?.id) &&
    subscription?.planId === SubscriptionStatus.TRIAL;

  const isPaidActive =
    Boolean(subscription?.id) &&
    subscription?.planId !== SubscriptionStatus.TRIAL &&
    !isLegacyCancelled;

  const isPlanTrial = isPaidPlanTrialPeriod(
    subscription as UserSubscription | undefined,
  );

  const showBillingPaymentDetails = isPaidActive || isFullyCancelled;

  const isStripeNonRenewingActive = isStripeNonRenewingActiveFn(
    subscription as UserSubscription | undefined,
    isPaidActive,
  );

  // Derive from sticky subscription so attention stays in sync during refetch.
  const billingAttentionReason = getBillingAttentionReason(
    subscription as UserSubscription | undefined,
  );

  const billingAttentionActionUrl = getBillingAttentionActionUrl(
    subscription ?? undefined,
    billingAttentionReason,
  );

  const invoiceLayout = isInvoiceBillingLayout(
    subscription as UserSubscription | null | undefined,
    showBillingPaymentDetails,
  );

  const isChfCurrency = isChfSubscriptionCurrency(
    subscription as UserSubscription | null | undefined,
  );

  const onEnterprisePaidPlan = isEnterprisePaidPlan(
    subscription as UserSubscription | null | undefined,
    isPaidActive,
  );

  const billingNextLabel = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (isFullyCancelled) {
      return t('subscriptions.profile.labels.subscriptionEndedOn');
    }
    if (
      sub?.stripeSubscriptionStatus === 'active' &&
      sub.stripeCancelAtPeriodEnd
    ) {
      return t('subscriptions.profile.labels.accessUntil');
    }
    if (sub?.stripeCollectionMethod === 'send_invoice') {
      return t('subscriptions.profile.labels.nextInvoice');
    }
    return nextPaymentLabel;
  }, [subscription, nextPaymentLabel, t, isFullyCancelled]);

  const billingCyclePrimaryText = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (!sub || !showBillingPaymentDetails) return '';
    if (
      sub.stripePriceUnitAmount != null &&
      sub.stripePriceCurrency &&
      sub.stripePriceInterval
    ) {
      const per =
        sub.stripePriceInterval === 'year'
          ? t('changePlan.perYear').trim()
          : sub.stripePriceInterval === 'month'
          ? t('changePlan.perMonth').trim()
          : '';

      const cycle =
        sub.stripePriceInterval === 'year'
          ? t('subscriptions.profile.labels.paymentCycleYear')
          : sub.stripePriceInterval === 'month'
          ? t('subscriptions.profile.labels.paymentCycleMonth')
          : translatedPaymentCycle;
      const stripeCycleText = `${cycle} · ${formatMoney(
        sub.stripePriceCurrency,
        sub.stripePriceUnitAmount,
      )}${per ? ` ${per}` : ''}`;
      if (isFullyCancelled) {
        return `${t(
          'subscriptions.profile.labels.lastPlan',
        )} · ${stripeCycleText}`;
      }
      return stripeCycleText;
    }

    if (translatedPaymentCycle || sub.subscriptionPlan) {
      const cycleText =
        translatedPaymentCycle && sub.subscriptionPlan
          ? `${translatedPaymentCycle} · ${formatMoney(
              sub.subscriptionPlan.currency,
              sub.frequency === SubscriptionFrequency.YEAR
                ? sub.subscriptionPlan.yearlyValue
                : sub.subscriptionPlan.monthlyValue,
            )}${
              sub.frequency === SubscriptionFrequency.YEAR
                ? ` ${t('changePlan.perYear').trim()}`
                : ` ${t('changePlan.perMonth').trim()}`
            }`
          : translatedPaymentCycle || '';
      if (isFullyCancelled && cycleText) {
        return `${t('subscriptions.profile.labels.lastPlan')} · ${cycleText}`;
      }
      return cycleText;
    }
    return translatedPaymentCycle || '';
  }, [
    subscription,
    showBillingPaymentDetails,
    isFullyCancelled,
    translatedPaymentCycle,
    t,
  ]);

  const invoiceContactEmail = getInvoiceContactEmail(
    subscription as UserSubscription | null | undefined,
  );

  const maxBucketSize = subscription?.maxStorage || 0;
  const maxCommunities = subscription?.maxCommunities || 0;
  const maxGroups = subscription?.maxGroups || 0;
  const maxUsers = subscription?.maxMembers || 0;

  const billingPortalUrl = getBillingPortalUrl(subscription ?? undefined);
  const hasStripeBillingAccount = Boolean(subscription?.subscriptionId?.trim());
  const canUseManageBilling =
    !isTrial &&
    Boolean(billingPortalUrl) &&
    (showBillingPaymentDetails || hasStripeBillingAccount);

  const isSubscriptionOwner = Boolean(
    profile?.userId &&
      subscription?.userId &&
      subscription.userId === profile.userId,
  );

  const showSwitchBillingMode = Boolean(
    isPaidActive &&
      isSubscriptionOwner &&
      !isFullyCancelled &&
      !onEnterprisePaidPlan &&
      subscription?.frequency === SubscriptionFrequency.YEAR &&
      subscription?.subscriptionId?.trim() &&
      (subscription as UserSubscription).stripeCollectionMethod &&
      ['charge_automatically', 'send_invoice'].includes(
        (subscription as UserSubscription).stripeCollectionMethod as string,
      ),
  );

  const paymentMethodLine = getPaymentMethodSummary(subscription ?? undefined);
  const cardExpiry = getCardExpiryParts(subscription ?? undefined);
  const lastInvoiceMeta = getLastInvoiceMeta(subscription ?? undefined);
  const lastInvoicePdfUrl = getLastInvoicePdfUrl(subscription ?? undefined);

  const usageInfo = subscription?.info;
  const usedMembers = getUsedMembersCount(subscription);
  const usedCommunities = Math.max(
    usageInfo?.activeCommunities ?? usageInfo?.communities ?? 0,
    ownedCommunitiesCount,
  );
  const usedGroups = Math.max(usageInfo?.groups ?? 0, groupsCount);
  const usedStorage = usageInfo?.bucketSize ?? 0;

  const scrollToInvoiceHistory = useCallback(() => {
    document
      .getElementById('invoice-history-heading')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return {
    subscription: subscription as UserSubscription | null | undefined,
    profile,
    subscriptionEndDate,
    translatedPaymentCycle,
    nextPaymentLabel,
    planName,
    isFullyCancelled,
    isLegacyCancelled,
    isTrial,
    isPaidActive,
    isPlanTrial,
    showBillingPaymentDetails,
    isStripeNonRenewingActive,
    billingAttentionReason,
    billingAttentionActionUrl,
    isInvoiceBillingLayout: invoiceLayout,
    isChfCurrency,
    onEnterprisePaidPlan,
    billingNextLabel,
    billingCyclePrimaryText,
    invoiceContactEmail,
    maxBucketSize,
    maxCommunities,
    maxGroups,
    maxUsers,
    billingPortalUrl,
    canUseManageBilling,
    isSubscriptionOwner,
    showSwitchBillingMode,
    paymentMethodLine,
    cardExpiry,
    lastInvoiceMeta,
    lastInvoicePdfUrl,
    usedMembers,
    usedCommunities,
    usedGroups,
    usedStorage,
    scrollToInvoiceHistory,
  };
}

export type SubscriptionProfileModel = ReturnType<
  typeof useSubscriptionProfileModel
>;
